// CloudSaveStore: SaveStore implementation backed by the `saves` table.
//
// Strategy:
//   - read() is sync (SaveStore contract). On boot we PRELOAD the cloud row
//     into a memory cache, then read() returns the cache.
//   - write() updates the cache synchronously and schedules a debounced
//     background upsert. Network failures only log; local UX never blocks.
//   - Conflict resolution: `client_rev` is monotonic; we only upsert if our
//     rev is strictly greater than what's stored.
//
// The local LocalSaveStore from Save.ts is still used as a write-through cache
// so the player can play offline without losing progress.

import type { SaveData, SaveStore } from "../systems/Save";
import { getSupabase } from "./client";
import { getUserId } from "./auth";

const FLUSH_DEBOUNCE_MS = 4000;
const LOCAL_MIRROR_KEY  = "mittelalte.save.v1"; // same key LocalSaveStore uses

export class CloudSaveStore implements SaveStore {
  private cache: SaveData | null = null;
  private flushTimer: number | null = null;
  private inflight = false;
  private dirty = false;

  /** MUST be awaited before installing the store. Pulls the cloud row. */
  async preload(): Promise<void> {
    const sb = getSupabase();
    const uid = getUserId();
    if (!sb || !uid) return;

    const { data, error } = await sb
      .from("saves")
      .select("data, client_rev")
      .eq("user_id", uid)
      .maybeSingle();

    if (error) {
      console.warn("[cloud-save] preload failed, falling back to local mirror", error);
      this.cache = readLocalMirror();
      return;
    }
    if (!data) {
      // First sign-in on this account. Promote the local mirror so existing
      // single-player progress isn't lost.
      this.cache = readLocalMirror();
      return;
    }

    const cloud = data.data as SaveData;
    const local = readLocalMirror();
    // Whichever has the higher clientRev wins. Cloud usually wins after sync.
    if (local && local.clientRev > (cloud.clientRev ?? 0)) {
      this.cache = local;
      this.dirty = true;
      this.scheduleFlush();
    } else {
      this.cache = cloud;
    }
  }

  read(): SaveData | null { return this.cache; }

  write(data: SaveData): void {
    this.cache = data;
    writeLocalMirror(data);     // always keep an offline copy
    this.dirty = true;
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flushNow();
    }, FLUSH_DEBOUNCE_MS);
  }

  /** Public so MenuScene can call on user sign-out / page hide. */
  async flushNow(): Promise<void> {
    if (this.inflight || !this.dirty || !this.cache) return;
    const sb = getSupabase();
    const uid = getUserId();
    if (!sb || !uid) return;

    this.inflight = true;
    const snapshot = this.cache;
    this.dirty = false;

    const { error } = await sb.from("saves").upsert({
      user_id:    uid,
      data:       snapshot,
      schema_ver: snapshot.version,
      client_rev: snapshot.clientRev,
    }, { onConflict: "user_id" });

    this.inflight = false;
    if (error) {
      console.warn("[cloud-save] upsert failed; will retry on next change", error);
      this.dirty = true; // try again next write
    }
  }
}

function readLocalMirror(): SaveData | null {
  try {
    const raw = localStorage.getItem(LOCAL_MIRROR_KEY);
    return raw ? (JSON.parse(raw) as SaveData) : null;
  } catch { return null; }
}

function writeLocalMirror(data: SaveData): void {
  try { localStorage.setItem(LOCAL_MIRROR_KEY, JSON.stringify(data)); }
  catch { /* quota — ignore */ }
}
